import React from 'react';
import { Column } from 'react-table';
import classes from './ContentModerationContainer.module.css';
import { ReactComponent as Comments } from '~/utils/images/comments.svg';
import { ReactComponent as Folder } from '~/utils/images/folder.svg';
import qs from 'querystring';
import { ReactComponent as FileIcon } from '~/utils/images/file.svg';
import { CocResult, CocStatus } from '~/api/openai';

// Export moderation notes link component to be used above the table
export const ModerationNotesLink = () => (
  <div className={classes.moderationNotesLink} style={{ marginBottom: '15px', textAlign: 'right' }}>
    <a
      href="https://docs.google.com/spreadsheets/d/1YWLUapQ23UFQWeM-B5IIfgk84S10bFjLdWadIPj2chA/edit?gid=0#gid=0"
      target="_blank"
      rel="noopener noreferrer"
      className="btn-ghost"
      style={{ display: 'inline-flex', alignItems: 'center' }}
    >
      <span style={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', textTransform: 'uppercase' }}>
        MODERATION NOTES
      </span>
      <Folder height={18} width={18} style={{ marginLeft: '6px' }} />
    </a>
  </div>
);

const getSentimentColumnClass = (sentiment: string) => {
  switch (sentiment) {
    case 'Positive':
      return classes.positiveSentiment;
    case 'Negative':
      return classes.negativeSentiment;

    default:
      return classes.undefinedSentiment;
  }
};

const getCroppedContent = (content: string) => {
  if (content.length <= 240) {
    return <>{content}</>;
  }

  return (
    <>
      {`${content.slice(0, 240)} ...`}
      <button className='btn-ghost'>View more</button>
    </>
  );
};

const escapeRegex = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');


const getHighlightedText = (content: string, terms: string[]) => {
  const normalizedTerms = Array.from(
    new Set(
      (terms || [])
        .map((term) => term?.trim())
        .filter((term): term is string => Boolean(term))
        .sort((a, b) => b.length - a.length)
    )
  );

  if (!normalizedTerms.length) return <>{content}</>;

  const regex = new RegExp(
    `(\\b(?:${normalizedTerms.map(escapeRegex).join('|')})\\b)`,
    'gi'
  );
  const matchSet = new Set(normalizedTerms.map((term) => term.toLowerCase()));
  return content.split(regex).map((part, index) =>
    matchSet.has(part.toLowerCase()) ? (
      <span key={`${part}-${index}`} className={classes.highlightWord}>
        {part}
      </span>
    ) : (
      <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
    )
  );
};

const getCroppedHighlightedContent = (content: string, terms: string[]) => {
  if (content.length <= 240) {
    return getHighlightedText(content, terms);
  }

  return (
    <>
      {getHighlightedText(`${content.slice(0, 240)} ...`, terms)}
      <button className='btn-ghost'>View more</button>
    </>
  );
};

export enum ContentStatus {
  HIDDEN = 'HIDDEN',
  VISIBLE = 'VISIBLE'
}

// 'placeComment' rows come from Places comments — they have no group, images,
// pinning, marketplace, or "great content" concept, so those actions/columns
// are hidden for them (see commentType checks below). The same goes for the
// Marketplace v2 rows: 'listing' (a listing itself) and 'listingComment',
// except that both can carry images.
export type CommentSourceType =
  | 'groupComment'
  | 'placeComment'
  | 'listing'
  | 'listingComment';

export const isListingRow = (row: { commentType: CommentSourceType }) =>
  row.commentType === 'listing' || row.commentType === 'listingComment';

export type FlaggedComment = {
  uuid: string;
  buildingId: string;
  buildingName: string;
  groupName: string;
  groupId?: string;
  placeId?: string;
  listingId?: string;
  listingTitle?: string;
  /** Listing rows only: integer cents, null for ISO. */
  priceCents?: number | null;
  /** The building has switched its old marketplace thread off. */
  marketplaceV2Enabled?: boolean;
  userName: string;
  authorName: string;
  reporterName: string;
  createdAt: {
    displayValue: string;
    value: string;
  };
  content: string;
  comments: string;
  status: ContentStatus;
  hiddenBy: string | null;
  hiddenAt: { displayValue: string; value: string } | null;
  hiddenByName: string | null;
  flaggedByUser: boolean;
  sentimentScore: string;
  confidenceRating: number;
  greatContent?: boolean;
  isPublicFacing?: boolean;
  parentId?: string | null;
  parentIsPublicFacing?: boolean | null;
  images: string[];
  buildingNotes: Array<string>;
  matchedTerms: { term: string; type: 'forbidden' | 'moderated' | 'alwaysHide' }[];
  cocVerdict: CocStatus | null;
  cocReasoning: string | null;
  commentType: CommentSourceType;
};

export type CommentModerationAction =
  | 'PIN_POST'
  | 'MOVE_TO_MARKETPLACE'
  | 'HIDE'
  | 'UNHIDE'
  | 'REVIEW_ALL_ABOVE'
  | 'MARK_REVIEWED'
  | 'TOGGLE_GREAT_CONTENT'
  | 'TOGGLE_PUBLIC_FACING';

const getCocStatusStyle = (status: CocStatus | undefined): React.CSSProperties => {
  switch (status) {
    case 'pass':
      return { color: '#2e7d32', fontWeight: 600 };
    case 'fail':
      return { color: '#c62828', fontWeight: 600 };
    case 'gray area':
      return { color: '#e65100', fontWeight: 600 };
    default:
      return {};
  }
};

const isEligibleForCocEvaluation = (row: FlaggedComment): boolean => {
  // Old-thread marketplace posts are mostly photos and prices; listings and
  // their comments are moderated text like any other post.
  if (row.groupName === 'Marketplace' && !isListingRow(row)) return false;
  const hasText = row.content && row.content.trim().length > 0;
  if (!hasText) return false; // image-only
  return true;
};

export const getColumnsForModerationTable = ({
  displayCommentDetails,
  onReviewAboveClicked,
  action,
  evaluateCoC,
  cocResults,
  cocLoading,
  onShowCocReason
}: {
  displayCommentDetails: (comment: FlaggedComment) => void;
  action: (type: CommentModerationAction, comment: FlaggedComment) => void;
  onReviewAboveClicked: (comment: FlaggedComment) => void;
  evaluateCoC: (comment: FlaggedComment) => void;
  cocResults: Record<string, CocResult>;
  cocLoading: Record<string, boolean>;
  onShowCocReason: (uuid: string) => void;
}): Column<any>[] => [
  {
    Header: 'Actions',
    disableSortBy: true,
    accessor: (row: FlaggedComment) => {
      return (
        <div>
          <div className={`${classes.tableColumn} ${classes.actionsColumn}`}>
            {row.status == ContentStatus.VISIBLE && (
              <button
                className={classes.redText}
                onClick={() => action('HIDE', row)}
              >
                HIDE
              </button>
            )}
            {row.status == ContentStatus.HIDDEN && (
              <button
                className={classes.greenText}
                onClick={() => action('UNHIDE', row)}
              >
                UNHIDE
              </button>
            )}
            <button
              className={classes.reviewButton}
              onClick={() => action('MARK_REVIEWED', row)}
            >
              REVIEW
            </button>
            {row.commentType === 'groupComment' && (
              <button
                className={classes.reviewButton}
                onClick={() => action('TOGGLE_GREAT_CONTENT', row)}
              >
                {row.greatContent ? 'UNMARK GREAT' : 'MARK GREAT'}
              </button>
            )}
            {/* A reply qualifies only once its parent is public: the snapshot
                nests by parent and drops any child whose parent is absent. */}
            {row.commentType === 'groupComment' &&
              (!row.parentId || row.parentIsPublicFacing) && (
                <button
                  className={classes.reviewButton}
                  onClick={() => action('TOGGLE_PUBLIC_FACING', row)}
                >
                  {row.isPublicFacing
                    ? 'REMOVE FROM WEBSITE'
                    : 'ADD TO WEBSITE'}
                </button>
              )}
            <button
              id={`reviewAboveButton-${row.uuid}`}
              className={classes.reviewAboveButton}
              onClick={() => onReviewAboveClicked(row)}
            >
              REVIEW ABOVE
            </button>
          </div>
          {row.commentType === 'groupComment' && (
            <div className={`${classes.tableColumn} ${classes.actionsColumn}`}>
              <button
                className={classes.reviewButton}
                onClick={() => action('PIN_POST', row)}
              >
                PIN POST
              </button>
              {row.groupName != 'Marketplace' && !row.marketplaceV2Enabled && (
                <button
                  className={classes.reviewButton}
                  onClick={() => action('MOVE_TO_MARKETPLACE', row)}
                >
                  MOVE TO MARKETPLACE
                </button>
              )}
            </div>
          )}
          {isEligibleForCocEvaluation(row) && (
            <div className={`${classes.tableColumn} ${classes.actionsColumn}`}>
              <button
                className={classes.reviewButton}
                onClick={() => evaluateCoC(row)}
                disabled={cocLoading[row.uuid]}
              >
                {cocLoading[row.uuid]
                  ? 'EVALUATING...'
                  : cocResults[row.uuid] || row.cocVerdict
                  ? 'RE-EVALUATE'
                  : 'EVALUATE AGAINST CODE'}
              </button>
            </div>
          )}
        </div>
      );
    }
  },
  {
    Header: 'Author',
    id: 'user',
    disableSortBy: true,
    accessor: (row: FlaggedComment) => {
      return (
        <div
          title={row.authorName}
          className={`${classes.tableColumn} ${classes.userName}`}
        >
          {row.authorName}
        </div>
      );
    }
  },
  {
    Header: 'Status',
    disableSortBy: true,
    accessor: (row: FlaggedComment) => {
      const visibleClassName =
        row.status == ContentStatus.HIDDEN
          ? classes.redText
          : classes.greenText;

      return (
        <div
          className={`${classes.tableColumn} ${classes.smallColumn} ${visibleClassName}`}
        >
          {row.status}
        </div>
      );
    }
  },
  {
    Header: 'Hidden By',
    id: 'hiddenBy',
    disableSortBy: true,
    width: '160px',
    accessor: (row: FlaggedComment) => {
      if (!row.hiddenBy) return null;

      return (
        <div className={`${classes.tableColumn} ${classes.hiddenByColumn}`}>
          <div>
            <span className={classes.hiddenByBadge}>{row.hiddenBy}</span>
          </div>
          {row.hiddenByName && <div>{row.hiddenByName}</div>}
          {row.hiddenAt && (
            <div className={classes.hiddenByTime}>
              {row.hiddenAt.displayValue}
            </div>
          )}
        </div>
      );
    }
  },
  {
    Header: 'Content',
    disableSortBy: true,
    width: '300px',
    accessor: (row: FlaggedComment) => {
      const highlightTerms = (row.matchedTerms || []).map((t) => t.term);
      return (
        <div
          className={`${classes.tableColumn} ${classes.contentColumn}`}
          onClick={() => displayCommentDetails(row)}
        >
          <div>{getCroppedHighlightedContent(row.content, highlightTerms)}</div>
          {Array.isArray(row.images) && row.images.length > 0 && (
            <div className={`margin-top-16`}>
              <div className={classes.contentImages}>
                {row.images[0] && (
                  <div
                    className='margin-left-8'
                    style={{ background: `url(${row.images[0]})` }}
                  />
                )}
                {row.images[1] && (
                  <div
                    className='margin-left-8'
                    style={{ background: `url(${row.images[1]})` }}
                  />
                )}
                {/* {row.images[0] && <img className='margin-left-8' src={row.images[0]} />} */}
              </div>
              {row.images.length > 1 && (
                <button className='btn-ghost'>
                  + {row.images.length - 2} images
                </button>
              )}
            </div>
          )}
        </div>
      );
    }
  },
  {
    Header: 'Pinned Notes',
    disableSortBy: true,
    width: '300px',

    accessor: (row: FlaggedComment) => {
      const notes = row.buildingNotes || [];
      if (!notes.length) return null;

      return (
        <div className={`${classes.tableColumn}`} style={{ width: '300px' }}>
          <div className={classes.buildingNotes} style={{ overflow: 'visible' }}>
            {notes.map((note: string, index: number) => (
              <div key={index} className={classes.noteItem} style={{ whiteSpace: 'normal' }}>
                - {note}
              </div>
            ))}
          </div>
        </div>
      );
    }
  },
  {
    Header: 'Comment',
    disableSortBy: true,
    accessor: (row: FlaggedComment) => {
      if (!row.buildingId) return null;

      // Place comments and listings have no dedicated comment view — link to the
      // building's Places / Listings page instead of the group Comments view.
      const href =
        row.commentType === 'placeComment'
          ? `/building/${row.buildingId}/places`
          : isListingRow(row)
          ? `/building/${row.buildingId}/listings`
          : `/building/${row.buildingId}/comments?${qs.stringify({
              commentId: row.uuid,
              ...(row.groupId && { groupId: row.groupId })
            })}`;

      return (
        <div className={`${classes.tableColumn} ${classes.smallColumn}`}>
          <a
            href={href}
            className='btn-ghost'
            target='_blank'
          >
            <Comments height={24} width={24} />
          </a>
          {row.comments}
        </div>
      );
    }
  },
  {
    Header: 'Flagged',
    disableSortBy: true,
    accessor: (row: FlaggedComment) => {
      const className = row.flaggedByUser && classes.redText;
      return (
        <div className={`${classes.tableColumn} ${className}`}>
          {row.flaggedByUser ? 'Flagged' : 'Not Flagged'}
        </div>
      );
    }
  },
  {
    Header: 'Who Flagged',
    disableSortBy: true,
    accessor: (row: FlaggedComment) => {
      const className = row.flaggedByUser && classes.redText;
      return (
        <div
          className={`${classes.tableColumn} ${className} ${classes.smallColumn}`}
        >
          {row.flaggedByUser ? 'USER' : 'BOT'}
        </div>
      );
    }
  },
  {
    Header: 'Reporter',
    disableSortBy: true,
    accessor: (row: FlaggedComment) => {
      const className = row.reporterName && classes.redText;
      return (
        <div className={`${classes.tableColumn} ${className}`}>
          {row.reporterName}
        </div>
      );
    }
  },
  {
    Header: 'Sentiment',
    disableSortBy: true,
    accessor: (row: FlaggedComment) => {
      const visibleClassName = getSentimentColumnClass(row.sentimentScore);

      return (
        <div className={`${classes.tableColumn} ${visibleClassName}`}>
          {row.sentimentScore || 'Undefined'}
        </div>
      );
    }
  },
  {
    Header: 'Score',
    disableSortBy: true,
    accessor: (row: FlaggedComment) => {
      return (
        <div className={`${classes.tableColumn} ${classes.smallColumn}`}>
          {Math.floor(row.confidenceRating * 100)}%
        </div>
      );
    }
  },
  {
    Header: 'Code of Conduct',
    disableSortBy: true,
    accessor: (row: FlaggedComment) => {
      const result =
        cocResults[row.uuid] ??
        (row.cocVerdict ? { status: row.cocVerdict, reasoning: row.cocReasoning ?? '' } : undefined);
      return (
        <div className={`${classes.tableColumn}`} style={{ display: 'flex', alignItems: 'center', gap: '6px', ...getCocStatusStyle(result?.status) }}>
          {result?.status ?? '-'}
          {result?.reasoning && (
            <button
              className='btn-ghost'
              style={{ padding: 0, lineHeight: 0 }}
              onClick={() => onShowCocReason(row.uuid)}
            >
              <FileIcon height={16} width={16} />
            </button>
          )}
        </div>
      );
    }
  },
  {
    Header: 'Building',
    disableSortBy: true,
    accessor: (row: FlaggedComment) => {
      return (
        <div className={`${classes.tableColumn} ${classes.building}`}>
          {row.buildingName}
        </div>
      );
    }
  },
  {
    Header: 'Group',
    disableSortBy: true,
    accessor: (row: FlaggedComment) => {
      return (
        <div className={`${classes.tableColumn} ${classes.groupColumn}`}>
          {row.groupName}
        </div>
      );
    }
  },
  {
    Header: 'When',
    id: 'when',
    disableSortBy: true,
    accessor: (row: FlaggedComment) => {
      return (
        <div className={`${classes.tableColumn} ${classes.contentCreatedAt}`}>
          {row.createdAt.displayValue}
        </div>
      );
    }
  },
];
